import { useMemo } from 'react';
import type { Draft } from '../domain/draft.ts';
import { PatternKind } from '../protocol/constants.ts';
import { PATTERN_LABELS } from './PatternSettings.tsx';

/** Preview only: macro coordinates come from real steps; generator shapes are illustrations. */
export function macroPreview(draft: Draft) {
  let x = 0, y = 0, buttons = 0;
  const points = [{ x, y, action: false }];
  for (const step of draft.steps) {
    if (!Number.isFinite(step.dx) || !Number.isFinite(step.dy)) continue;
    x += step.dx;
    y += step.dy;
    points.push({ x, y, action: step.buttons !== buttons });
    buttons = step.buttons;
  }
  const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const scale = Math.min(420 / Math.max(1, maxX - minX), 200 / Math.max(1, maxY - minY));
  return points.map((p) => ({
    x: 300 + (p.x - (minX + maxX) / 2) * scale,
    y: 150 + (p.y - (minY + maxY) / 2) * scale,
    action: p.action,
  }));
}

export function PatternPreview({ draft }: { draft: Draft }) {
  const points = useMemo(() => macroPreview(draft), [draft]);
  const first = points[0]!;
  return <figure className="production__preview">
    <svg viewBox="0 0 600 300" role="img" aria-label={`${PATTERN_LABELS[draft.kind]} pattern preview`}>
      <g className="production__preview-guides" aria-hidden="true">
        <path d="M60 150H540M300 30V270" />
        <circle cx="300" cy="150" r="100" />
      </g>
      <g className="production__preview-line" aria-hidden="true">
        {draft.kind === PatternKind.MACRO ? <>
          <polyline points={points.map((p) => `${p.x},${p.y}`).join(' ')} />
          <circle className="production__preview-origin" cx={first.x} cy={first.y} r="4" />
          {points.filter((p) => p.action).map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="5" />)}
        </> : draft.kind === PatternKind.CIRCLE ? <circle cx="300" cy="150" r="98" />
          : draft.kind === PatternKind.ZIGZAG ? <path d="M160 150H440m-12 -8 12 8-12 8M172 142l-12 8 12 8" />
            : draft.kind === PatternKind.DRIFT ? <path d="M180 210L420 90m-14 0h14v14" />
              : <path d="M300 150l-24 -16 42 -27 26 21-12 28-48 10-20 30 51 9 25-24-10-26 25-38-38-33-58 28-16 45" />}
      </g>
    </svg>
    <figcaption>{draft.kind === PatternKind.MACRO ? 'Scaled preview · relative movement' : 'Shape preview'}</figcaption>
  </figure>;
}
